// @ts-strict-ignore
import {apiClient} from 'common/util/api-client';
import {MinimalWorkspace, Workspace} from 'app_api/projects/types';
import {GrantStatus} from 'common/report/util/saved-entities/types';

import {appApiFetchJson, appApiUrl} from '../utils';
import {
  UnifiedMembershipType,
  WorkspaceApiResponse,
  WorkspaceApiUnifiedMember,
  WorkspaceCreateResponse,
  WorkspaceGetPublicResponse,
  WorkspaceListResponse,
  WorkspaceTeam,
} from './workspaces-client-types';

export interface EventUserPropertyFilter {
  operator: `and` | `or`;
  children: Array<Obj | EventUserPropertyFilter>;
}

export interface EventNameFilter {
  inclusionType: FilterInclusionType;
  filter: string[];
}

export interface WorkspaceDataFilter {
  eventNames: EventNameFilter;
  eventProperties: EventUserPropertyFilter;
  userProperties: EventUserPropertyFilter;
}

export const enum FilterDataType {
  EventNames = `event_names`,
  EventProperties = `event_properties`,
  UserProperties = `user_properties`,
}

export const enum FilterInclusionType {
  Exclude = `exclude`,
  Include = `include`,
}

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  status: string;
}

export interface ProjectMember {
  id: number;
  name: string;
  email: string;
  role: string;
  roleOrder: number;
  targetType: string;
  grantStatus?: GrantStatus;
}

interface WorkspaceMembersAndTeams {
  userEmailList?: string[];
  teamIds?: number[];
}

export type UnifiedMember = UnifiedIndividualMember | UnifiedOrgMember | UnifiedTeamMember;

export interface UnifiedBaseMember {
  id: number;
  name: string;
  invited: boolean;
  email: string;
  role?: string;
  entityType: string;
}

export interface UnifiedTeamMember extends UnifiedBaseMember {
  membershipType: UnifiedMembershipType.Team;
  teamName: string;
}

export interface UnifiedOrgMember extends UnifiedBaseMember {
  membershipType: UnifiedMembershipType.Organization;
  orgName: string;
}

export interface UnifiedIndividualMember extends UnifiedBaseMember {
  membershipType: UnifiedMembershipType.Individual;
}

export interface WorkspaceObj {
  id: number;
  canUpdateBasic?: boolean;
  canUpdateRestricted?: boolean;
  canUpdateVisibility?: boolean;
  createdAt: Date;
  creatorId: number;
  creator: string;
  dataFilters: WorkspaceDataFilter;
  description: string;
  hasAllOrgMembers: boolean;
  isGlobal: boolean;
  isDefault: boolean;
  isRestricted: boolean;
  isVisible: boolean;
  projectId: number;
  membershipApprovalRequired: boolean;
  /** List of organizations who have workspace[dataview] access and the current user is a member of */
  requestingUserOrgs: string[];
  /** List of teams who have workspace[dataview] access and the current user is a member of */
  requestingUserTeams: string[];
  unifiedMemberList: UnifiedMember[];
  name: string;
  teamList: WorkspaceTeam[];
}

export interface TeamObj extends WorkspaceTeam {
  orgId: number;
  orgRole: string;
  members: TeamMember[];
  grantStatus?: GrantStatus;
}

// API is defined: app_api/projects/workspaces/views.py
class WorkspacesClient {
  buildEndpoint({projectId, workspaceId}: {projectId: number; workspaceId?: number}) {
    let endpoint = `workspaces`;
    if (workspaceId) {
      endpoint += `/${workspaceId}`;
    }
    return appApiUrl(endpoint, {projectId});
  }

  async getWorkspaces({projectId}): Promise<WorkspaceObj[]> {
    const response = await apiClient.get<WorkspaceListResponse>(this.buildEndpoint({projectId}));
    return response.map(toWorkspaceObj);
  }

  async getWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.get<WorkspaceApiResponse>(this.buildEndpoint({projectId, workspaceId}));
    return toWorkspaceObj(response);
  }

  async getPublicWorkspaces(projectId: number): Promise<WorkspaceObj[]> {
    const response = await apiClient.get<WorkspaceGetPublicResponse>(`${this.buildEndpoint({projectId})}/public`);
    return response.map(toWorkspaceObj);
  }

  async joinWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceApiResponse>(
      `${this.buildEndpoint({projectId, workspaceId})}/join`,
      null,
    );
    return toWorkspaceObj(response);
  }

  async leaveWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceApiResponse>(
      `${this.buildEndpoint({projectId, workspaceId})}/leave`,
      null,
    );
    return toWorkspaceObj(response);
  }

  async updateWorkspace(projectId: number, workspaceId: number, updates: Partial<WorkspaceObj>): Promise<WorkspaceObj> {
    const response = await apiClient.patch<WorkspaceCreateResponse>(
      `${this.buildEndpoint({projectId})}/${workspaceId}`,
      {
        ...toWorkspaceApiObj(updates),
        id: workspaceId,
      },
    );
    return toWorkspaceObj(response);
  }

  getAvailableMembers(projectId: number, serviceAccounts = false): Promise<ProjectMember[]> {
    return appApiFetchJson(
      appApiUrl(`granted-users`, {projectId}),
      {method: `get`},
      // eslint-disable-next-line camelcase
      {
        include_service_account_users: serviceAccounts,
      },
    ).then((response: any) => {
      if (serviceAccounts) {
        response.members = response.members.filter((member) => member.entity_type === `service_account`);
      }
      return response.members.map((member) => ({
        id: parseInt(member.id, 10),
        name: member.name,
        email: member.email,
        role: member.role,
        roleOrder: member.role_order,
        targetType: member.target_type,
      }));
    });
  }

  getAvailableTeams(projectId: number): Promise<TeamObj[]> {
    return apiClient.get(appApiUrl(`granted-teams`, {projectId})).then((response: any) => response.teams);
  }

  async addWorkspaceMembersAndTeams(
    projectId: number,
    workspaceId: number,
    {userEmailList = [], teamIds = []}: WorkspaceMembersAndTeams,
  ) {
    const response = await apiClient.post<WorkspaceApiResponse>(
      `${this.buildEndpoint({projectId, workspaceId})}/upsert-members`,
      {
        user_emails: userEmailList,
        team_ids: teamIds,
        id: workspaceId,
      },
    );
    return toWorkspaceObj(response);
  }

  async removeWorkspaceMembersAndTeams(
    projectId: number,
    workspaceId: number,
    {userEmailList = [], teamIds = []}: WorkspaceMembersAndTeams,
  ) {
    const response = await apiClient.post<WorkspaceApiResponse>(
      `${this.buildEndpoint({projectId, workspaceId})}/delete-members`,
      {
        user_emails: userEmailList,
        team_ids: teamIds,
        id: workspaceId,
      },
    );
    return toWorkspaceObj(response);
  }

  async updateDataFilters(projectId: number, workspaceId: number, dataFilters: any) {
    return this.updateWorkspace(projectId, workspaceId, {dataFilters});
  }

  async createWorkspace(
    projectId: number,
    {name, description, isRestricted = false, isVisible = false},
  ): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceCreateResponse>(
      `${this.buildEndpoint({projectId})}`,
      toWorkspaceApiObj({
        name,
        description,
        isVisible,
        isRestricted,
      }),
    );
    return toWorkspaceObj(response);
  }

  deleteWorkspace(projectId, workspaceId: number): Promise<string> {
    return apiClient.delete<string>(`${this.buildEndpoint({projectId})}/${workspaceId}`);
  }

  deleteWorkspaces(projectId, workspaceIds: number[]): Promise<string> {
    return apiClient.post<string>(`${this.buildEndpoint({projectId})}/bulk-delete`, {
      workspace_ids: workspaceIds,
    });
  }

  async setDefaultWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceCreateResponse>(
      `${this.buildEndpoint({projectId, workspaceId})}/set-default`,
      {},
    );
    return toWorkspaceObj(response);
  }
}

export function individualMembershipGrant(member: UnifiedMember) {
  return member.membershipType === UnifiedMembershipType.Individual;
}

export function memberIsTraditionalUser(member: UnifiedMember) {
  return member.entityType === `user`;
}

export function memberIsServiceAccount(member: UnifiedMember) {
  return member.entityType === `service_account`;
}

type WorkspaceApiObj = any;
function toWorkspaceApiObj(workspace: Partial<WorkspaceObj>): WorkspaceApiObj {
  const apiObj: WorkspaceApiObj = {};

  if (workspace.id) {
    apiObj.id = workspace.id;
  }
  if (workspace.projectId) {
    apiObj.project_id = workspace.projectId;
  }
  if (workspace.name) {
    apiObj.name = workspace.name;
  }
  if (workspace.dataFilters) {
    apiObj.data_filters = workspace.dataFilters;
  }
  if (workspace.description !== undefined) {
    apiObj.description = workspace.description || ``;
  }
  if (workspace.unifiedMemberList && workspace.unifiedMemberList.some(individualMembershipGrant)) {
    // in order to update individual grants to a workspace, user_emails is used and should only be passed individual grant types
    apiObj.user_emails = workspace.unifiedMemberList.filter(individualMembershipGrant).map((m) => m.email);
  }
  if (workspace.teamList) {
    apiObj.team_ids = workspace.teamList.map((t) => t.id);
  }
  if (workspace.isDefault !== undefined) {
    apiObj.is_default = workspace.isDefault;
  }
  if (workspace.isGlobal !== undefined) {
    apiObj.is_global = workspace.isGlobal;
  }
  if (workspace.isRestricted !== undefined) {
    apiObj.is_restricted = workspace.isRestricted;
  }
  if (workspace.isVisible !== undefined) {
    apiObj.is_visible = workspace.isVisible;
  }
  return apiObj;
}

function isWorkspaceApiResponse(workspace: any): workspace is WorkspaceApiResponse {
  return [`can_update_basic`, `can_update_restricted`, `can_update_visibility`].every((prop) =>
    Object.prototype.hasOwnProperty.call(workspace, prop),
  );
}

const COPYABLE_PROPS: {[key: string]: keyof MinimalWorkspace} = {
  creator: `creator_name`,
  description: `description`,
  id: `id`,
  name: `name`,
  projectId: `project_id`,
  isDefault: `is_default`,
  isGlobal: `is_global`,
};
export function extendWorkspace(workspace, apiAttrs: WorkspaceApiResponse | Workspace | MinimalWorkspace) {
  const newWorkspace = {...workspace};
  for (const [prop, apiName] of Object.entries(COPYABLE_PROPS)) {
    if (apiName in apiAttrs) {
      newWorkspace[prop] = apiAttrs[apiName];
    }
  }
  return newWorkspace;
}

export function toWorkspaceObj(apiResponse: WorkspaceApiResponse | Workspace): WorkspaceObj {
  const minimalProps = extendWorkspace({}, apiResponse);

  const modifyProps = isWorkspaceApiResponse(apiResponse)
    ? {
        canUpdateBasic: apiResponse.can_update_basic,
        canUpdateRestricted: apiResponse.can_update_restricted,
        canUpdateVisibility: apiResponse.can_update_visibility,
      }
    : {};

  return {
    ...minimalProps,

    dataFilters: {
      eventNames: {
        inclusionType:
          (apiResponse.data_filters?.event_names?.inclusion_type as FilterInclusionType) ?? FilterInclusionType.Include,
        filter: apiResponse.data_filters?.event_names?.filter ?? [],
      },
      userProperties: {operator: `and`, children: [], ...apiResponse.data_filters?.user_properties},
      eventProperties: {operator: `and`, children: [], ...apiResponse.data_filters?.event_properties},
    },
    teamList: apiResponse.team_list ?? [],
    createdAt: new Date(apiResponse.created_iso),
    creatorId: apiResponse.creator_id,
    isRestricted: apiResponse.is_restricted,
    isVisible: apiResponse.is_visible,
    hasAllOrgMembers: apiResponse.has_all_org_members,
    membershipApprovalRequired: apiResponse.membership_approval_required,
    requestingUserOrgs: apiResponse.requesting_user_orgs ?? [],
    requestingUserTeams: apiResponse.requesting_user_teams ?? [],
    unifiedMemberList: (apiResponse.unified_member_list ?? []).map<UnifiedMember>(
      (unifiedMemberApiResp: WorkspaceApiUnifiedMember) => {
        const {id, name, invited, email} = unifiedMemberApiResp;
        const base = {id, name, invited, email, entityType: unifiedMemberApiResp.entity_type};
        switch (unifiedMemberApiResp.membership_type) {
          case UnifiedMembershipType.Individual:
            return {...base, membershipType: UnifiedMembershipType.Individual};
          case UnifiedMembershipType.Organization:
            return {
              ...base,
              membershipType: UnifiedMembershipType.Organization,
              orgName: unifiedMemberApiResp.org_name,
            };
          case UnifiedMembershipType.Team:
            return {...base, membershipType: UnifiedMembershipType.Team, teamName: unifiedMemberApiResp.team_name};
        }
      },
    ),
    ...modifyProps,
  };
}

export const workspacesClient = new WorkspacesClient();
